import {addDays, addMonths} from 'date-fns';
import {isFeatureEnabled} from 'feature-flags';
import {useLocalStorage} from 'platform/components';
import {match} from 'ts-pattern';

import {all, isNil, isNotEmpty, values} from 'ramda';
import {isArray, isString, isValidDate} from 'ramda-adjunct';

import {useReadCodelistQuery} from '@dms/api/core';
import {useListRentalCalendarEventsQuery} from '@dms/api/rental';
import {useListRentalCalendarVehiclesQuery} from '@dms/api/vehicle';
import {browserStorageKey} from '@dms/config';
import {featureFlags} from '@dms/feature-flags';

import {getApiDateString, parseDate, useQueryState} from 'shared/web';

import {VEHICLE_BLOCK_CODELIST_ID} from '../constants/vehicleBlockCodelistId';
import {VEHICLE_CATEGORY_CODELIST_ID} from '../constants/vehicleCategoryCodelistId';
import {
  EMPTY_RENTAL_CALENDAR_FILTERS,
  type RentalCalendarFilterValues,
} from '../types/RentalCalendarFilterValues';
import type {RentalCalendarView} from '../types/RentalCalendarView';
import {getCalendarScale} from '../utils/getCalendarScale';
import {mapCalendarEventsFromApi} from '../utils/mapCalendarEventsFromApi';
import {useCanCreateVehicleBlock} from './useCanCreateVehicleBlock';

export function useRentalOrderCalendarControl() {
  const [selectedDateParam, setSelectedDateParam] = useQueryState(SELECTED_DATE_KEY);
  const [viewParam, setViewParam] = useQueryState(VIEW_KEY);

  const [storedFilters, setFilters] = useLocalStorage<RentalCalendarFilterValues>(
    browserStorageKey.RENTAL_CALENDAR_FILTERS,
    EMPTY_RENTAL_CALENDAR_FILTERS
  );

  // A filter added after a selection was stored is absent from the restored object, and every
  // consumer relies on each key holding an array
  const restoredFilters = {...EMPTY_RENTAL_CALENDAR_FILTERS, ...storedFilters};

  // `useLocalStorage` casts raw JSON to the type without checking it, and a stale or hand-edited
  // entry survives a reload, so a bad shape would throw on every load with no way out
  const filters = values(restoredFilters).every(isStringArray)
    ? restoredFilters
    : EMPTY_RENTAL_CALENDAR_FILTERS;

  const view: RentalCalendarView = viewParam === 'day' ? 'day' : 'month';

  const dateFromParam = parseDate(selectedDateParam);
  const parsedSelectedDate = isValidDate(dateFromParam) ? dateFromParam : new Date();

  const scale = getCalendarScale(view, parsedSelectedDate);

  const canCreateVehicleBlock = useCanCreateVehicleBlock();

  const {
    data: vehicles,
    isLoading: areVehiclesLoading,
    isFetching: areVehiclesFetching,
    isError: areVehiclesErrored,
    refetch: refetchVehicles,
  } = useListRentalCalendarVehiclesQuery({
    vehicleCategory: toFilterParam(filters.vehicleCategory),
    carPark: toFilterParam(filters.carPark),
    vehicleType: toFilterParam(filters.vehicleType),
    make: toFilterParam(filters.make),
    modelFamily: toFilterParam(filters.modelFamily),
    bodyStyle: toFilterParam(filters.bodyStyle),
    fuelType: toFilterParam(filters.fuelType),
    transmission: toFilterParam(filters.transmission),
    drive: toFilterParam(filters.drive),
    doorCount: toFilterParam(filters.doorCount),
    seatCount: toFilterParam(filters.seatCount),
  });

  const {
    data: apiEvents,
    isFetching: areEventsFetching,
    isError: areEventsErrored,
    refetch: refetchEvents,
  } = useListRentalCalendarEventsQuery(
    {
      from: getApiDateString(scale.rangeStart),
      to: getApiDateString(scale.rangeEnd),
      vehicleId: toFilterParam(filters.vehicleId),
    },
    {refetchOnMountOrArgChange: true, pollingInterval: EVENTS_POLLING_INTERVAL}
  );

  const {data: vehicleCategories} = useReadCodelistQuery({
    codelistId: VEHICLE_CATEGORY_CODELIST_ID,
  });

  // Category ids only become group headings once the codelist resolves them to names
  const vehicleCategoryLabels = new Map(
    (vehicleCategories?.codes ?? []).map((code) => [code.codeId, code.name])
  );

  const areVehicleBlocksEnabled = isFeatureEnabled(featureFlags.RENTAL_VEHICLE_BLOCKS);

  const {data: blockReasons} = useReadCodelistQuery(
    {codelistId: VEHICLE_BLOCK_CODELIST_ID},
    {skip: !areVehicleBlocksEnabled}
  );

  // The feed returns blocks regardless of the flag, so they are filtered out here. A canceled
  // order no longer holds the vehicle, so it must leave its slot free for a new rental.
  const visibleApiEvents = apiEvents?.filter(
    (event) => (areVehicleBlocksEnabled || event.type !== 'block') && event.state !== CANCELED_STATE
  );

  const blockReasonLabels = new Map(
    (blockReasons?.codes ?? []).map((code) => [code.codeId, code.name])
  );

  const onNavigate = (direction: 'previous' | 'next' | 'today') => {
    const newDate = match(direction)
      .with('today', () => new Date())
      .with('previous', () =>
        view === 'month' ? addMonths(parsedSelectedDate, -1) : addDays(parsedSelectedDate, -1)
      )
      .with('next', () =>
        view === 'month' ? addMonths(parsedSelectedDate, 1) : addDays(parsedSelectedDate, 1)
      )
      .exhaustive();

    setSelectedDateParam(getApiDateString(newDate));
  };

  /** Jump to any date from the header date picker; the current view is kept */
  const onDateChange = (date: Date) => {
    setSelectedDateParam(getApiDateString(date));
  };

  const onViewChange = (newView: RentalCalendarView) => {
    setViewParam(newView);
  };

  /** Pull both feeds again so the current view shows what other users have just changed */
  const onRefresh = () => {
    refetchVehicles();
    refetchEvents();
  };

  /** Drill down from a month column header into that day's hour view */
  const onDayDrillDown = (day: Date) => {
    setSelectedDateParam(getApiDateString(day));
    setViewParam('day');
  };

  // The vehicles endpoint has no vehicle filter, so the selection narrows the rows here
  const visibleVehicles = isNotEmpty(filters.vehicleId)
    ? vehicles?.filter((vehicle) => filters.vehicleId.includes(vehicle.vehicleId))
    : vehicles;

  return {
    view,
    vehicleCategoryLabels,
    blockReasonLabels,
    canCreateVehicleBlock,
    scale,
    selectedDate: parsedSelectedDate,
    filters,
    vehicles: visibleVehicles,
    events: mapCalendarEventsFromApi(visibleApiEvents, vehicles),
    // Only the first row load blanks the calendar — moving to another period keeps the grid up
    // and fills the tracks with skeletons instead
    isLoading: areVehiclesLoading,
    // Skeletons only stand in for a period that has nothing to draw yet
    areEventsPending: areEventsFetching && isNil(apiEvents),
    // A period already in the RTK cache renders instantly and would otherwise refetch with no
    // feedback at all, so every in-flight request drives the progress bar
    isRefreshing: areEventsFetching || areVehiclesFetching,
    isError: areVehiclesErrored || areEventsErrored,
    onNavigate,
    onDateChange,
    onViewChange,
    onDayDrillDown,
    onRefresh,
    onFiltersChange: setFilters,
  };
}

/**
 * An empty multiselect means "no restriction" and must not reach the query string. Sorting keeps
 * the same selection on one RTK cache entry regardless of the order the options were picked in.
 */
const isStringArray = (value: unknown) => isArray(value) && all(isString, value);

const toFilterParam = (values: string[]) => (isNotEmpty(values) ? [...values].sort() : undefined);

/** Canceled orders are hidden from the calendar so their slot can be booked again */
const CANCELED_STATE = 'canceled';

/** Query parameter key for storing current calendar date */
const SELECTED_DATE_KEY = 'selectedDate';

/** Query parameter key for storing the calendar view (month | day) */
const VIEW_KEY = 'calendarView';

/** Polling interval keeping the calendar fresh while the tab stays open */
const EVENTS_POLLING_INTERVAL = 15 * 60 * 1000;
